import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { getStocks, StockDto } from '../../services/stocksApi';
import { formatEuro, formatPercent, formatQuantity, formatCryptoQuantity, formatNumber } from '../../utils/currency';
import { batchAnalyze, PositionAnalysis } from './analysis/stockAnalysis';
import { deleteStock } from '../../services/stocksApi';
import { InvestmentType } from '../../types/stocks';
import AddETFDialog from './AddETFDialog';
import StockDetailsDialog from './StockDetailsDialog';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import SellIcon from '@mui/icons-material/Sell';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddPurchaseDialog from './AddPurchaseDialog';
import EtfAnalyticsDialog from './EtfAnalyticsDialog';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import PageHeader from '../../components/layout/PageHeader';
import AutoGrid from '../../components/layout/AutoGrid';

// Using centralized InvestmentType enum from types

interface ETFAnalysis extends PositionAnalysis {
  original: StockDto;
  assetClass: string;
  expenseRatio: number;
}

const ETFsPage: React.FC = () => {
  const [etfs, setEtfs] = useState<ETFAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [detailsStockId, setDetailsStockId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [stockToDelete, setStockToDelete] = useState<ETFAnalysis | null>(null);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseTargetId, setPurchaseTargetId] = useState<string | null>(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState<ETFAnalysis | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [etfToEdit, setEtfToEdit] = useState<ETFAnalysis | null>(null);
  const [editSymbol, setEditSymbol] = useState('');
  const [editPrice, setEditPrice] = useState<string>('');
  // removed cambio editing; FX resolved server-side
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editOriginalSymbol, setEditOriginalSymbol] = useState('');
  const [editOriginalPrice, setEditOriginalPrice] = useState<string>('');
  
  const [successMessage, setSuccessMessage] = useState('ETF criado com sucesso');

  const wrapEtf = useCallback((stock: StockDto, analysis: PositionAnalysis): ETFAnalysis => ({
    ...analysis,
    original: stock,
    assetClass: determineAssetClass(stock.symbol),
    expenseRatio: 0
  }), []);

  const loadETFData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all stocks and filter for ETFs
      const allStocks = await getStocks();
      // Filter by explicit investmentType from backend now that it's available
      const etfStocks = allStocks.filter(stock => stock.investmentType === InvestmentType.ETF);
      const analyses = batchAnalyze(etfStocks).map((a, idx) => wrapEtf(etfStocks[idx], a));
      setEtfs(analyses);
      
    } catch (err) {
      console.error('Error loading ETF data:', err);
      setError('Não foi possível carregar os dados dos ETFs');
    } finally {
      setLoading(false);
    }
  }, [wrapEtf]);

  useEffect(() => {
    loadETFData();
  }, [loadETFData]);

  // Analysis reused from shared stock analysis util

  const determineAssetClass = (symbol: string): string => {
    // Simplified asset class determination based on symbol
    const assetClasses = {
      'SPY': 'EUA - grandes empresas',
      'QQQ': 'EUA - tecnologia',
      'IWM': 'EUA - pequenas empresas',
      'EFA': 'Mercados desenvolvidos internacionais',
      'EEM': 'Mercados emergentes',
      'AGG': 'Obrigações dos EUA',
      'TLT': 'Dívida pública de longo prazo',
      'GLD': 'Ouro/matérias-primas',
      'VTI': 'Mercado total dos EUA',
      'VOO': 'EUA - grandes empresas',
      'VXUS': 'Internacional'
    };
    return assetClasses[symbol.toUpperCase() as keyof typeof assetClasses] || 'Desconhecida';
  };

  const formatCurrency = (amount: number): string => formatEuro(amount); // EUR aggregate totals
  const formatShares = (qty: number): string => formatQuantity(qty);
  const formatPercentage = (percentage: number): string => formatPercent(percentage);

  const handleOpenDetails = (etf: ETFAnalysis) => {
    setDetailsStockId(etf.stockPublicId);
    setDetailsOpen(true);
  };

  const handleSell = (etf: ETFAnalysis) => {
    // Navigate to sales page by symbol
    window.location.href = `/stocks/sales?stock=${encodeURIComponent(etf.symbol)}`;
  };

  const handleBuy = (etf: ETFAnalysis) => {
    setPurchaseTargetId(etf.stockPublicId);
    setPurchaseDialogOpen(true);
  };

  const handleAnalytics = (etf: ETFAnalysis) => {
    setAnalyticsData(etf);
    setAnalyticsOpen(true);
  };

  const handleDeleteClick = (etf: ETFAnalysis) => {
    setStockToDelete(etf);
    setDeleteConfirmOpen(true);
  };

  const openEdit = (etf: ETFAnalysis) => {
    setEtfToEdit(etf);
    setEditSymbol(etf.symbol.toUpperCase());
    const priceStr = etf.original.currentPrice !== undefined && etf.original.currentPrice !== null ? String(etf.original.currentPrice) : '';
    setEditPrice(priceStr);
    setEditOriginalSymbol(etf.symbol.toUpperCase());
    setEditOriginalPrice(priceStr);
    setEditError(null);
    setEditDialogOpen(true);
  };

  const parsedEditPrice = editPrice === '' ? undefined : Number(editPrice);
  const normalizedSymbol = editSymbol.trim().toUpperCase();

  const isUnchanged = (
    etfToEdit !== null &&
    normalizedSymbol === editOriginalSymbol &&
    (parsedEditPrice === (editOriginalPrice === '' ? undefined : Number(editOriginalPrice)))
  );

  const getValidationError = (): string | null => {
    if (!normalizedSymbol) return 'O símbolo é obrigatório';
    if (editPrice !== '' && (isNaN(Number(editPrice)) || Number(editPrice) < 0)) return 'O preço não pode ser negativo';
    return null;
  };
  const validationError = getValidationError();

  const handleEditSubmit = async () => {
    if (!etfToEdit) return;
    if (validationError || isUnchanged) return; // extra safety
    setEditSubmitting(true);
    setEditError(null);
    try {
      const { updateStock } = await import('../../services/stocksApi');
      await updateStock(etfToEdit.stockPublicId, {
        symbol: normalizedSymbol,
        currentPrice: parsedEditPrice,
        investmentType: InvestmentType.ETF
      });
      setEditDialogOpen(false);
      setEtfToEdit(null);
      setSuccessMessage('ETF atualizado com sucesso');
      setSuccessOpen(true);
      await loadETFData();
    } catch (e: any) {
      setEditError(e?.message || 'Não foi possível atualizar o ETF');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    if (editSubmitting) return;
    setEditDialogOpen(false);
    setEtfToEdit(null);
  };

  const handleDeleteConfirm = async () => {
    if (!stockToDelete) return;
    try {
      await deleteStock(stockToDelete.stockPublicId);
      setDeleteConfirmOpen(false);
      setStockToDelete(null);
      loadETFData();
    } catch (e) {
      console.error('Failed to delete ETF', e);
      setDeleteConfirmOpen(false);
      setStockToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setStockToDelete(null);
  };

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Carteira de ETFs"
        subtitle="Os fundos negociados em bolsa (ETF) dão exposição diversificada a várias classes de ativos"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Adicionar ETF
          </Button>
        }
      />

      {/* Summary Cards */}
      <AutoGrid min={240} gap={3} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total de ETFs
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="30%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {etfs.length}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Valor total dos ETFs
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {formatCurrency(etfs.reduce((sum, etf) => sum + etf.currentValueEur, 0))}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AnalyticsIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Lucro/Prejuízo total dos ETFs
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography 
                variant="h6" 
                component="div"
                color={etfs.reduce((sum, etf) => sum + etf.gainLossNoDivEur, 0) >= 0 ? 'success.main' : 'error.main'}
              >
                {formatCurrency(etfs.reduce((sum, etf) => sum + etf.gainLossNoDivEur, 0))}
              </Typography>
            )}
          </CardContent>
        </Card>
      </AutoGrid>

      {/* ETF Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Símbolo</TableCell>
                <TableCell>Classe de ativos</TableCell>
                <TableCell align="right">Unidades</TableCell>
                <TableCell align="right">Preço</TableCell>
                <TableCell align="right">Preço médio (EUR)</TableCell>
                <TableCell align="right">Investido (EUR)</TableCell>
                <TableCell align="right">Valor atual (EUR)</TableCell>
                <TableCell align="right">Lucro/Prejuízo (sem div.)</TableCell>
                <TableCell align="right">Lucro/Prejuízo (com div.)</TableCell>
                <TableCell align="right">Dividendos (EUR)</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Loading skeleton rows
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              ) : etfs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box sx={{ py: 4 }}>
                      <BusinessIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Nenhum ETF encontrado
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Adicione o seu primeiro ETF para acompanhar a sua carteira diversificada
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                etfs.map((etf) => (
                  <TableRow key={etf.symbol} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight="bold">
                          {etf.symbol}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={etf.assetClass} 
                        size="small" 
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatShares(etf.totalShares)}
                    </TableCell>
                    <TableCell align="right">
                      {etf.original.currentPrice != null ? (
                        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency((etf.original.currentPrice ?? 0) * (etf.original.currentCambio ?? 1))}
                          </Typography>
                          {String(etf.original.currency).toUpperCase() !== 'EUR' && (
                            <Typography variant="caption" color="text.secondary">
                              {getCurrencySymbol(etf.original.currency)} {formatCoinDecimals(Number(etf.original.currentPrice ?? 0), String(etf.original.currency), etf.original.investmentType === InvestmentType.Cryptocurrency)}
                            </Typography>
                          )}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(etf.averagePriceEur)}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                        <Typography variant="body2" fontWeight="bold">{formatCurrency(etf.totalInvestedEur)}</Typography>
                        {String(etf.original.currency).toUpperCase() !== 'EUR' && (
                          <Typography variant="caption" color="text.secondary">
                            {getCurrencySymbol(etf.original.currency)} {formatCoinDecimals(etf.totalInvested, String(etf.original.currency), etf.original.investmentType === InvestmentType.Cryptocurrency)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                        <Typography variant="body2" fontWeight="bold">{formatCurrency(etf.currentValueEur)}</Typography>
                        {String(etf.original.currency).toUpperCase() !== 'EUR' && (
                          <Typography variant="caption" color="text.secondary">
                            {getCurrencySymbol(etf.original.currency)} {formatCoinDecimals(etf.currentValue, String(etf.original.currency), etf.original.investmentType === InvestmentType.Cryptocurrency)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {etf.gainLossNoDivEur >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                        )}
                        <Box>
                          <Typography 
                            variant="body2" 
                            color={etf.gainLossNoDivEur >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {formatCurrency(etf.gainLossNoDivEur)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color={etf.gainLossNoDivEur >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatPercentage(etf.gainLossNoDivEurPct)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {etf.gainLossWithDivEur >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                        )}
                        <Box>
                          <Typography 
                            variant="body2" 
                            color={etf.gainLossWithDivEur >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {formatCurrency(etf.gainLossWithDivEur)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color={etf.gainLossWithDivEur >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatPercentage(etf.gainLossWithDivEurPct)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(etf.dividends)}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display:'flex', justifyContent:'flex-end' }}>
                        <Tooltip title="Detalhes">
                          <IconButton size="small" onClick={() => handleOpenDetails(etf)}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Análise">
                          <IconButton size="small" onClick={() => handleAnalytics(etf)}>
                            <AnalyticsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Comprar / Adicionar compra">
                          <IconButton size="small" color="primary" onClick={() => handleBuy(etf)}>
                            <AddShoppingCartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Vender">
                          <IconButton size="small" color="warning" onClick={() => handleSell(etf)}>
                            <SellIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(etf)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(etf)}>
                            <EditIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ETF-Specific Insights */}
      {!loading && etfs.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resumo da carteira de ETFs
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Diversificação por classe de ativos:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {new Set(etfs.map(etf => etf.assetClass)).size} classes de ativos diferentes
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    ETF com melhor desempenho:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {etfs.length > 0 ? 
                      etfs.reduce((best, current) => 
                        current.gainLossPercentage > best.gainLossPercentage ? current : best
                      ).symbol : 'N/D'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
      <AddETFDialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)} 
        onCreated={() => { loadETFData(); setSuccessMessage('ETF criado com sucesso'); setSuccessOpen(true); }}
        existingSymbols={etfs.map(e => e.symbol)}
      />
      <AddPurchaseDialog
        open={purchaseDialogOpen}
        stockPublicId={purchaseTargetId}
        onClose={() => setPurchaseDialogOpen(false)}
        onCreated={() => { setPurchaseDialogOpen(false); loadETFData(); }}
      />
      <StockDetailsDialog open={detailsOpen} stockId={detailsStockId} onClose={() => setDetailsOpen(false)} />
      <EtfAnalyticsDialog open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} data={analyticsData && {
        symbol: analyticsData.symbol,
        totalShares: analyticsData.totalShares,
        averagePrice: analyticsData.averagePrice,
        totalInvested: analyticsData.totalInvested,
        currentValue: analyticsData.currentValue,
        gainLoss: analyticsData.gainLoss,
        gainLossPercentage: analyticsData.gainLossPercentage,
        dividends: analyticsData.dividends
      }} />
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Eliminar ETF</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem a certeza de que quer eliminar o ETF {stockToDelete?.symbol}? Isto remove-o e remove também as transações associadas.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancelar</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={successOpen} autoHideDuration={4000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert onClose={() => setSuccessOpen(false)} severity="success" elevation={6} variant="filled">
          {successMessage}
        </MuiAlert>
      </Snackbar>
      <Dialog open={editDialogOpen} onClose={handleEditCancel} fullWidth maxWidth="sm">
        <DialogTitle>Editar ETF</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          {validationError && !editError && (
            <Alert severity="warning" sx={{ mb: 2 }}>{validationError}</Alert>
          )}
          <TextField
            label="Símbolo"
            value={editSymbol}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditSymbol(e.target.value.toUpperCase())}
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 10 }}
            error={!!validationError && !normalizedSymbol}
            helperText={!normalizedSymbol ? 'Obrigatório' : ' '}
          />
          <TextField
            label="Preço atual"
            value={editPrice}
            type="number"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditPrice(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ step: '0.01', min: '0' }}
            error={editPrice !== '' && (isNaN(Number(editPrice)) || Number(editPrice) < 0)}
            helperText={editPrice !== '' && (isNaN(Number(editPrice)) || Number(editPrice) < 0) ? 'É necessário um número não negativo' : ' '}            
          />
          <TextField
            label="Tipo de investimento"
            value={InvestmentType.ETF}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
            helperText="Fixo como ETF"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel} disabled={editSubmitting}>Cancelar</Button>
          <Button 
            onClick={handleEditSubmit} 
            disabled={editSubmitting || !!validationError || isUnchanged}
            variant="contained"
          >
            {editSubmitting ? 'A guardar...' : isUnchanged ? 'Sem alterações' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ETFsPage;

function getCurrencySymbol(code?: string | null): string {
  const c = String(code || '').toUpperCase();
  switch (c) {
    case 'USD': return '$';
    case 'GBP': return '£';
    case 'JPY': return '¥';
    case 'CHF': return 'CHF';
    case 'CAD': return 'C$';
    case 'AUD': return 'A$';
    case 'BRL': return 'R$';
    case 'CNY': return '¥';
    case 'INR': return '₹';
    case 'EUR': return '€';
    default: return c || '';
  }
}

function formatCoinDecimals(amount: number, code?: string | null, isCrypto?: boolean): string {
  const c = String(code || '').toUpperCase();
  if (isCrypto) {
    return formatCryptoQuantity(amount);
  }
  if (c === 'USD' || c === 'EUR') return formatNumber(amount, 2);
  return formatNumber(amount, 4);
}
